import AdyenCheckout from '@adyen/adyen-web';
import UIElement from '@adyen/adyen-web/dist/types/components/UIElement';
import { PaymentAction } from '@adyen/adyen-web/dist/types/types';
import { useCallback, useEffect, useMemo, useState } from 'react';

import AdobeTrackingService from '@services/AdobeTrackingService';
import { captureException } from '@services/sentryService';
import { Maybe } from '@types-internal';
import { AdyenConfig } from '@types-internal/AdyenTypes';
import { handleActionCreatorFailureRedirect, handleQrCodeFailureRedirect } from '@utils/redirect';
import { getMobileAppType, isRecognizedApp } from '@utils/retryUtils';

interface ActionCreatorConfig extends AdyenConfig {
  locale: string;
  paymentId?: string;
}
export type BancontactMobileDetails = { payload: string };
export type ThreeDSDetails = { threeDSResult: string };
export type Details = BancontactMobileDetails | ThreeDSDetails;
export type PaymentDetailsState = { data?: { details?: Details } };

const translations = (locale: string) => {
  return {
    [locale]: {},
  };
};

export const ACTION_CREATOR_CONTAINER = 'action_creator_container';

const MAX_RETRIES = 2;
const RETRY_WAIT = 2000;
const ACTION_CREATOR_TIMEOUT = 1000 * 60 * 15;

const useActionCreator = (paymentAction: PaymentAction, config: ActionCreatorConfig) => {
  const { adyenEnvironment, locale, paymentId, clientKey } = config;

  const [details, setDetails] = useState<Maybe<Details>>();
  const [, setActionTimeoutId] = useState<NodeJS.Timeout>();
  const [retryCount, setRetryCount] = useState(0);
  const [isActionTimedOut, setActionTimedOut] = useState<boolean>(false);
  const [mobileAppType] = useState(() => {
    return getMobileAppType();
  });
  const isMobileApp = isRecognizedApp(mobileAppType) ?? false;

  const onAdditionalDetails = useCallback((state: PaymentDetailsState) => {
    setActionTimeoutId((timerId) => {
      clearTimeout(timerId);
      return undefined;
    });
    if (!isActionTimedOut) setDetails(state?.data?.details);
  }, []);

  const paymentActionMemo = useMemo(
    () => paymentAction,
    [
      paymentAction?.type,
      paymentAction?.paymentMethodType,
      paymentAction?.url,
      paymentAction?.paymentData,
      paymentAction?.authorisationToken,
    ],
  );

  useEffect(() => {
    let action: Maybe<UIElement>;
    let retryTimeoutId: Maybe<NodeJS.Timeout>, actionTimeOutId: Maybe<NodeJS.Timeout>;
    const onError = (error: any) => {
      if (error?.errorCode === 'timeout') {
        setActionTimedOut(true);
        clearTimeout(actionTimeOutId);
        return;
      }
      AdobeTrackingService.adyenTracking('3DSsubmitKO');
      captureException({
        error: {
          name: error.name,
          message: error.message,
          stack: error.stack,
          error,
          errorCustomMessage: '3DS error captured',
          paymentId,
        },
      });
      retryTimeoutId = setTimeout(() => {
        setRetryCount((count) => count + 1);
      }, RETRY_WAIT);
    };

    async function initialize() {
      try {
        const adyen = await AdyenCheckout({
          environment: adyenEnvironment,
          translations: translations(locale),
          locale,
          clientKey,
          onError,
          onAdditionalDetails,
        });
        if (!adyen) {
          throw new Error('could not create Adyen action object authorization component');
        }
        const threeDSConfiguration = {
          challengeWindowSize: '05', // ['100%', '100%']
        };
        action = adyen
          .createFromAction(paymentActionMemo, threeDSConfiguration)
          .mount(`#${ACTION_CREATOR_CONTAINER}`);

        // Send tracking event if this 3ds challenge page is displayed
        if (paymentActionMemo.type === 'threeDS2') {
          AdobeTrackingService.adyenTracking('3DSdisplay');
        }

        actionTimeOutId = setTimeout(() => setActionTimedOut(true), ACTION_CREATOR_TIMEOUT);
        setActionTimeoutId(actionTimeOutId);
      } catch (error) {
        onError(error);
      }
    }

    if (retryCount < MAX_RETRIES) {
      initialize();
    } else {
      if (isMobileApp) handleActionCreatorFailureRedirect();
      else handleQrCodeFailureRedirect();
    }

    return () => {
      action?.unmount().remove();
      clearTimeout(retryTimeoutId);
      clearTimeout(actionTimeOutId);
    };
  }, [adyenEnvironment, locale, clientKey, paymentActionMemo, onAdditionalDetails, retryCount]);

  return { details, isActionTimedOut };
};

export default useActionCreator;
